import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { EARNING_APPS, type EarningApp } from "@/lib/earning-apps/apps";
import { listCustomEarningApps } from "@/lib/earning-apps/admin.functions";
import { SITE_URL } from "@/lib/seo";

const TITLE = "Earning Apps Review — Real Or Fake, Honest Verdicts | DrHint";
const DESCRIPTION =
  "Independent Earning Apps Review directory. Every app is installed, tested to full payout, and rated Real, Mixed, Risky or Likely Fake by DrHint's editorial desk.";

export const Route = createFileRoute("/earning-apps-review/")({
  loader: async () => {
    try {
      const res = await listCustomEarningApps();
      return { custom: res.items };
    } catch {
      return { custom: [] as EarningApp[] };
    }
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE_URL}/earning-apps-review` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/earning-apps-review` }],
    scripts: [
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "DrHint Earning Apps Review Directory",
          numberOfItems: EARNING_APPS.length,
          itemListElement: EARNING_APPS.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: a.name,
            url: `${SITE_URL}/earning-apps-review/${a.slug}`,
          })),
        }),
      },
    ],
  }),
  component: HubPage,
});

const VERDICT_ORDER = ["Real", "Mixed", "Risky", "Likely Fake"] as const;
const VERDICT_STYLE: Record<(typeof VERDICT_ORDER)[number], string> = {
  Real: "bg-emerald-500/15 text-emerald-500 ring-emerald-500/40",
  Mixed: "bg-amber-500/15 text-amber-500 ring-amber-500/40",
  Risky: "bg-orange-500/15 text-orange-400 ring-orange-500/40",
  "Likely Fake": "bg-red-500/15 text-red-500 ring-red-500/40",
};

function HubPage() {
  const { custom } = Route.useLoaderData();
  const seen = new Set(EARNING_APPS.map((a) => a.slug));
  const merged = useMemo(
    () => [...EARNING_APPS, ...custom.filter((a: EarningApp) => !seen.has(a.slug))],
    [custom],
  );
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return merged;
    return merged.filter((a) =>
      a.name.toLowerCase().includes(needle) ||
      a.category.toLowerCase().includes(needle) ||
      a.slug.includes(needle) ||
      a.verdict.toLowerCase().includes(needle),
    );
  }, [needle, merged]);
  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-6">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:text-primary">Home</Link> / Earning Apps Review
        </nav>
        <h1 className="text-3xl md:text-5xl font-black tracking-tight">
          Earning Apps Review — Honest, First-Hand Verdicts
        </h1>
        <p className="mt-4 text-muted-foreground max-w-3xl">
          Every app in this directory is installed on a real device, used for at least 30 days,
          and pushed to a full payout cycle. No affiliate money. No paid placements. Just an
          honest <b>Real / Mixed / Risky / Likely Fake</b> verdict from the DrHint desk.
        </p>
        <div className="mt-6 relative max-w-2xl">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 25+ apps — Honeygain, Swagbucks, Hamster Kombat, CashKaro…"
            aria-label="Search earning apps"
            className="w-full rounded-xl border border-input bg-background px-4 py-3 pr-24 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {filtered.length} / {merged.length}
          </span>
        </div>
      </section>

      {needle && filtered.length === 0 && (
        <section className="mx-auto max-w-5xl px-4 mt-6">
          <div className="rounded-2xl border border-border bg-card/50 p-6 text-sm">
            No app matched <b>"{q}"</b>. Try a shorter keyword like <b>honey</b>, <b>survey</b>, or <b>crypto</b>.
          </div>
        </section>
      )}

      {VERDICT_ORDER.map((verdict) => {
        const bucket = filtered.filter((a) => a.verdict === verdict);
        if (bucket.length === 0) return null;
        return (
          <section key={verdict} className="mx-auto max-w-5xl px-4 mt-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 ${VERDICT_STYLE[verdict]}`}>
                {verdict}
              </span>
              <span className="text-sm text-muted-foreground">{bucket.length} apps</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {bucket.map((a) => (
                <Link
                  key={a.slug}
                  to="/earning-apps-review/$slug"
                  params={{ slug: a.slug }}
                  className="block rounded-2xl border border-border/60 bg-card/50 p-4 hover:border-primary/60 hover:bg-primary/5 transition-colors"
                >
                  <div className="text-base font-bold">{a.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{a.category}</div>
                  <div className="text-xs mt-2">
                    <span className="text-muted-foreground">Min payout:</span>{" "}
                    <span className="font-semibold">{a.minPayout}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      <section className="mx-auto max-w-3xl px-4 mt-16 mb-16 text-sm text-muted-foreground">
        <p>
          Missing an app? Email <b>hello@drhint.com</b> and we will add it to the review queue.
          We publish a new verdict on this hub every 2–4 weeks.
        </p>
      </section>
    </Layout>
  );
}